import * as React from 'react';

import { useClient } from '@playbooks/contexts';
import { Button } from '@playbooks/ui/components/button';
import { FarIcon, Icon } from '@playbooks/ui/components/icon';
import { Link } from '@playbooks/ui/components/link';

type AccountNavigationViewItem = {
	label: string;
	icon: string;
	iconType?: 'fab' | 'fad' | 'far' | 'fas';
	href?: string;
	onClick?: () => void;
	external?: boolean;
	selected?: boolean;
};

type AccountNavigationViewProps = {
	title: string;
	items: AccountNavigationViewItem[];
	target?: string;
	onBack: () => void;
};

function AccountNavigationView({ title, items, target, onBack }: AccountNavigationViewProps) {
	const { platformHref } = useClient();

	// Render
	return (
		<div data-slot='account-navigation-view' className='min-h-0'>
			<div className='flex-start min-h-10 gap-2 border-b border-border px-2 py-1.5'>
				<Button data-menu-contained type='button' variant='ghost' size='icon-xs' aria-label='Back' onClick={onBack}>
					<FarIcon icon='chevron-left' />
				</Button>
				<h2 className='type-label'>{title}</h2>
			</div>
			<div data-slot='account-navigation-items' className='space-y-0.5 p-1'>
				{items.map(item => {
					const content = (
						<>
							<Icon type={item.iconType || 'far'} icon={item.icon} />
							<span className='flex-1 text-left'>{item.label}</span>
							{item.selected ? <FarIcon icon='check' className='text-xs' /> : null}
							{item.external ? <FarIcon icon='arrow-up-right' className='size-3 text-foreground-body' /> : null}
						</>
					);
					return item.href ? (
						<Link
							key={item.label}
							href={item.external ? item.href : platformHref(item.href)}
							target={item.external ? '_blank' : target}
							rel={item.external ? 'noopener noreferrer' : undefined}
							variant='ghost'
							data-active={item.selected}
							className='w-full justify-start'>
							{content}
						</Link>
					) : (
						<Button
							key={item.label}
							type='button'
							variant='ghost'
							data-active={item.selected}
							className='w-full justify-start'
							onClick={item.onClick}>
							{content}
						</Button>
					);
				})}
			</div>
		</div>
	);
}

export { AccountNavigationView };
export type { AccountNavigationViewItem };
